import { createContext, useContext } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useLocalStorage } from './useLocalStorage';
import {
    signIn,
    signUp,
    sendCustomChallengeAnswer,
    currentSession,
    currentAuthenticatedUser,
    confirmSignIn,
    signOut,
    updateUserAttributes,
    autoSignIn,
    getCurrentUser,
    fetchAuthSession,
} from 'aws-amplify/auth';
import { query } from '../services/salesforce';
import { get, post } from '../api';
import { getMD5 } from '../utils';
import Intercom from '@intercom/messenger-js-sdk';
import { UPDATE_CUSTOMER_ORDERS_PATH, ORDER_UPDATE_APP } from '../pages/UpdateCustomerOrders/constants';

const AuthContext = createContext();

const randomDigits = (number) => {
    const randomValues = new Uint8Array(number);
    window.crypto.getRandomValues(randomValues);
    return Array.from(randomValues).map(intToHex).join('');
};

const intToHex = (number) => {
    return number.toString(16).padStart(2, '0');
};

const getAllUrlParams = (url) => {
    let queryString = url ? url.split('?')[1] : window.location.search.slice(1);

    let obj = {};

    if (queryString) {
        queryString = queryString.split('#')[0];

        let arr = queryString.split('&');

        for (let i = 0; i < arr.length; i++) {
            let a = arr[i].split('=');

            let paramName = a[0];
            let paramValue = typeof a[1] === 'undefined' ? true : a[1];

            if (paramName.match(/\[(\d+)?\]$/)) {
                let key = paramName.replace(/\[(\d+)?\]/, '');
                if (!obj[key]) obj[key] = [];

                if (paramName.match(/\[\d+\]$/)) {
                    let index = /\[(\d+)\]/.exec(paramName)[1];
                    obj[key][index] = paramValue;
                } else {
                    obj[key].push(paramValue);
                }
            } else {
                if (!obj[paramName]) {
                    obj[paramName] = paramValue;
                } else if (obj[paramName] && typeof obj[paramName] === 'string') {
                    obj[paramName] = [obj[paramName]];
                    obj[paramName].push(paramValue);
                } else {
                    obj[paramName].push(paramValue);
                }
            }
        }
    }

    return obj;
};

// Resolves the `app` context for custom/getInitialDetails. A non-blank value makes
// the Salesforce side skip the Portal-Enabled gate. The /update-customer-orders flow
// uses ORDER_UPDATE_APP so a Contact that exists in Salesforce can sign in even when
// it is not Portal-Enabled; that contact is then confined to the billing page.
const getAppContext = (path) => {
    const targetPath = path || (typeof window !== 'undefined' ? window.location.pathname : '');
    if (targetPath && targetPath.startsWith(UPDATE_CUSTOMER_ORDERS_PATH)) {
        return ORDER_UPDATE_APP;
    }
    return window.location.hostname?.startsWith('tax.') ? 'tax' : '';
};

export const AuthProvider = ({ children, application }) => {
    const [user, setUser] = useLocalStorage('user', null);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const redirectLocation = location.state?.path || '/';
    const answerSearchParams = location.state?.searchParams;

    const passwordLessLogin = async (data) => {
        let paramsData = redirectLocation ? getAllUrlParams(redirectLocation) : {};
        let type = paramsData.type ? paramsData.type : '';
        let recordId = paramsData.recordId ? paramsData.recordId : '';
        let email = data.email.toLowerCase().trim();
        let authenticated = await isAuthenticated();
        if (authenticated) {
            navigate('/', {
                replace: true,
            });
            return;
        }
        const params = {
            username: email,
            password: data.password ? data.password : randomDigits(10),
            options: {
                validationData: { type: type ? type : '' },
                autoSignIn: true,
            },
        };

        let hasError = false;
        try {
            await signUp(params);
        } catch (e) {
            if (e.message.indexOf('Email not found') > -1) {
                hasError = true;
            }
            console.log(e);
        }
        if (!hasError) {
            try {
                const thisUser = await signIn({
                    username: email,
                    options: {
                        authFlowType: 'CUSTOM_WITHOUT_SRP',
                    },
                });
                setUser(thisUser);
                let existingSearchParmas = location?.state?.searchParams ? location?.state?.searchParams : {};
                navigate('/email-otp', {
                    replace: true,
                    state: {
                        path: redirectLocation,
                        contactData: data.contactData,
                        email: email,
                        searchParams: {
                            simulation: searchParams.get('simulation'),
                            type: type,
                            recordId: recordId,
                            ...existingSearchParmas,
                        },
                    },
                });
            } catch (e) {
                console.log(e);
                setTimeout(() => window.location.reload(), 2000);
            }
        } else {
            if (data.callback) {
                data.callback(true);
            }
        }
    };
    const navigateToLocation = (userDetails) => {
        let isNavigated = false;
        if (window.location.search) {
            isNavigated = true;
            navigate('/?recordId=' + window.location.search, { replace: true });
        } else {
            if (userDetails.form) {
                isNavigated = true;
                navigate('/?recordId=' + userDetails.form.id, { replace: true });
            } else {
                if (userDetails.forms?.length > 0) {
                    isNavigated = true;
                    navigate('/?recordId=' + userDetails.forms[0].id);
                }
            }
        }

        if (!isNavigated) {
            navigate('/', { replace: true });
        }
    };

    const otpVerification = async ({ otp, email, memberId, userDetails, callback }) => {
        try {
            let params = { otp: otp, email: email, memberId: memberId };

            await post('otp/verifyOTP', params).then((response) => {
                if (response.isSuccess) {
                    userDetails.memberProfile.Email__c = email;
                    setUserDetailsNotAsync(userDetails);
                    if (!userDetails?.memberProfile?.Mobile_Phone_Number__c && !userDetails?.memberProfile?.Skipped_Mobile_Updation__c) {
                        navigate('/phone-number' + window.location.search, {
                            replace: true,
                        });
                    } else {
                        navigateToLocation(userDetails);
                    }
                } else {
                    callback(response.errorMessage);
                }
            });
        } catch (e) {
            console.log('otpVerification error', e);
            callback(e.message);
        }
    };

    const phoneOtpVerification = async ({ otp, phoneNumber, memberId, userDetails, callback }) => {
        try {
            let params = { otp: otp, phoneNumber: phoneNumber, memberId: memberId };

            await post('otp/verifyPhoneOTP', params).then((response) => {
                if (response.isSuccess) {
                    // userDetails.memberProfile.PhoneNumber__c = phoneNumber;
                    // setUserDetailsNotAsync(userDetails);
                    navigate('/' + window.location.search, { replace: true });
                } else {
                    callback(response.errorMessage);
                }
            });
        } catch (e) {
            console.log('phone otp Verification error', e);
            callback(e.message);
        }
    };

    const resendPhoneOtp = async ({ phoneNumber, memberId, callback }) => {
        try {
            let params = { phoneNumber: phoneNumber, memberId: memberId };

            // write resend otp api code here
            await post('otp/fake-resend-phone-api', params).then((response) => {
                if (response.isSuccess) {
                    // navigate('/' + window.location.search, { replace: true });
                    callback(null, response.isSuccess);
                } else {
                    callback(response.errorMessage);
                }
            });
        } catch (e) {
            console.log('resendOtp error', e);
            callback(e.message);
        }
    };

    const resendOtp = async ({ email, memberId, callback }) => {
        try {
            let params = { email: email, memberId: memberId };

            if (email) {
                post('otp/generateOTP', { memberId: memberId, email }).then((result) => {
                    if (result.isSuccess) {
                        callback(null, result.isSuccess);
                    } else {
                        callback(result.errorMessage);
                    }
                });
            }
        } catch (e) {
            console.log('resendOtp error', e);
            callback(e.message);
        }
    };

    const answer = async (userInfo, answer, callback) => {
        try {
            let user = userInfo;
            if (user.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
                const output = await confirmSignIn({ challengeResponse: answer });
                if (output.isSignedIn) {
                    console.log(output);
                    const sessionData = (await fetchAuthSession()) ?? {};
                    setUser(sessionData);
                    const userData = await getUserDetails(location.state?.path);
                    setUser(userData);

                    // Intercom({
                    //     app_id: 'xs8rskx8',
                    //     user_id: userData.contact.id, // IMPORTANT: Replace "user.id" with the variable you use to capture the user's ID
                    //     name: userData.contact.name, // IMPORTANT: Replace "user.name" with the variable you use to capture the user's name
                    //     email: userData?.contact?.email, // IMPORTANT: Replace "user.createdAt" with the variable you use to capture the user's sign-up date in a Unix timestamp (in seconds) e.g. 1704067200
                    //     created_at: Math.floor(new Date('2024-01-01').getTime() / 1000),
                    //     user_hash: userData?.contact?.contactHash,
                    // });
                    const isOrderUpdateFlow = (location.state?.path || '').startsWith(UPDATE_CUSTOMER_ORDERS_PATH);
                    if (userData && userData?.contact && (userData?.contact?.relatedAccounts?.length > 0 || window.location.hostname?.startsWith('tax.') || isOrderUpdateFlow)) {
                        if (location.state?.path) {
                            navigate(location.state?.path, {
                                replace: true,
                                state: { path: location.state?.path },
                            });
                        } else {
                            navigate('/', {
                                replace: true,
                                state: { path: location.state?.path },
                            });
                        }
                    } else {
                        callback("Sorry! We don't recognize this email. Please contact our support team.");
                        setTimeout(() => {
                            navigate('/signout', {
                                replace: true,
                                state: {
                                    path: redirectLocation,
                                    searchParams: { simulation: searchParams.get('simulation') },
                                },
                            });
                        }, 4 * 1000);
                    }
                } else {
                    callback('Not a valid code. Please try again.');
                }
            }
        } catch (e) {
            console.log(e);
            callback('There was a problem logging in. Please contact support.');
            if (e.message) {
                setTimeout(() => {
                    navigate('/signout', {
                        replace: true,
                        state: {
                            path: redirectLocation,
                            searchParams: { simulation: searchParams.get('simulation') },
                        },
                    });
                }, 4 * 1000);
            }
        }
    };
    const login = async (data) => {
        try {
            const thisUser = await signIn({ username: data.username, password: data.password });
            setUser(thisUser);
            navigate(redirectLocation, { replace: true });
        } catch (e) {
            console.log(e);
            setTimeout(() => window.location.reload(), 2000);
        }
    };

    const logout = async () => {
        await signOut();
        setUser(null);
        clearLocalStorage();
        navigate('/login' + (location.state?.query ? location.state?.query : ''), {
            replace: true,
            state: { path: location.state?.path },
        });
    };

    const logoutWithoutNavigate = async () => {
        await signOut();
        setUser(null);
        clearLocalStorage();
    };

    const clearLocalStorage = () => {
        const keyToKeep = 'user-payload';

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key !== keyToKeep) {
                localStorage.removeItem(key);
                i--; // Adjust index because localStorage size changed
            }
        }
    };

    const isAuthenticated = async () => {
        try {
            const userDetails = await getCurrentUser();
            if (!userDetails) {
                return false;
            }

            let userData = getUserDetailsNotAsync();
            if (userData && userData?.contact && (userData?.businessList?.length > 0 || window.location.hostname?.startsWith('tax.'))) {
                return true;
            }
            return false;
        } catch {
            return false;
        }
    };

    const isSimpleAuthenticated = async () => {
        try {
            const userDetails = await getCurrentUser();
            return userDetails ? true : false;
        } catch {
            return false;
        }
    };

    // Authenticated for the confined /update-customer-orders page: a valid Cognito
    // session plus a resolved Salesforce Contact. Unlike isAuthenticated(), this does
    // NOT require a business list, so a non-Portal-Enabled contact can use this page.
    const isOrderUpdateAuthenticated = async () => {
        try {
            const userDetails = await getCurrentUser();
            if (!userDetails) {
                return false;
            }
            const userData = getUserDetailsNotAsync();
            return !!(userData && userData?.contact && userData?.contact?.id);
        } catch {
            return false;
        }
    };

    // True when the logged-in contact is NOT Portal-Enabled. These sessions are
    // confined to /update-customer-orders; MainLayout bounces them away from the
    // rest of the portal even if the URL is changed by hand.
    const isConfinedToOrderUpdate = async () => {
        try {
            const userDetails = await getCurrentUser();
            if (!userDetails) {
                return false;
            }
            const userData = getUserDetailsNotAsync();
            const appContext = getAppContext(location.state?.path);
            if (appContext === ORDER_UPDATE_APP) {
                return !!(userData && userData?.contact && userData?.contact?.id && userData?.contact?.portalEnabled !== true);
            }

            return false;
        } catch {
            return false;
        }
    };

    const getUserDetailsNotAsync = () => {
        let jwtToken = user?.userSub;
        if (jwtToken) {
            const userData = localStorage.getItem(jwtToken);
            if (userData) {
                return JSON.parse(userData);
            }
        }
        return '';
    };

    const setUserDetailsNotAsync = (details) => {
        let jwtToken = user?.userSub;
        if (jwtToken) {
            localStorage.setItem(jwtToken, JSON.stringify(details));
        }
        return '';
    };

    const refreshSessionData = async () => {
        let isAuth = await isAuthenticated();
        if (!isAuth) {
            navigate('/signout' + window.location.search, {
                replace: true,
            });
            return;
        }
        if (user) {
            let jwtToken = user.userSub;
            if (jwtToken) {
                let result;
                let currentUserDetail = getUserDetailsNotAsync();

                let userData = currentUserDetail;
                let sessionData = (await fetchAuthSession()) ?? {};

                let email = userData.contact.email;

                result = await post(`custom/getInitialDetails`, {
                    email: email,
                    payload: localStorage.getItem('user-payload') && application === 'preview' ? JSON.parse(localStorage.getItem('user-payload')) : '',
                    //payload: localStorage.getItem('user-payload') ? JSON.parse(localStorage.getItem('user-payload')) : '',
                    app: window.location.hostname?.startsWith('tax.') ? 'tax' : '',
                });

                if (result.isSuccess && result.data) {
                    userData = { ...sessionData, contact: result.data };
                    if (!window.location.hostname?.startsWith('tax.')) {
                        userData = { ...userData, businessList: result.data.relatedAccounts?.filter((item) => item.accountId !== result.data.accountId) };
                    }
                    userData.application = application;
                    setUser(userData);
                }
                let failed = false;
                try {
                    localStorage.setItem(jwtToken, JSON.stringify(userData));
                } catch (error) {
                    failed = true;
                    localStorage.removeItem(jwtToken);
                    localStorage.removeItem('user');
                }

                if (failed) {
                    localStorage.setItem(jwtToken, JSON.stringify(userData));
                }

                return userData;
            }
        }
    };

    const getUserDetails = async (appContextPath) => {
        let sessionData = (await fetchAuthSession()) ?? {};
        setUser(sessionData);
        let jwtToken = sessionData.userSub;
        if (jwtToken) {
            const userData = localStorage.getItem(jwtToken);
            if (userData) {
                return JSON.parse(userData);
            } else {
                let result;
                let userData = sessionData;

                let email = sessionData.tokens.idToken.payload['email'];

                result = await post(`custom/getInitialDetails`, {
                    email: email,
                    payload: localStorage.getItem('user-payload') && application === 'preview' ? JSON.parse(localStorage.getItem('user-payload')) : '',
                    //payload: localStorage.getItem('user-payload') ? JSON.parse(localStorage.getItem('user-payload')) : '',
                    app: getAppContext(appContextPath),
                });

                if (result.isSuccess && result.data) {
                    userData = { ...sessionData, contact: result.data };
                    if (!window.location.hostname?.startsWith('tax.')) {
                        userData = { ...userData, businessList: result.data.relatedAccounts?.filter((item) => item.accountId !== result.data.accountId) };
                    }

                    userData.application = application;
                    setUser(userData);
                }
                let failed = false;
                try {
                    localStorage.setItem(jwtToken, JSON.stringify(userData));
                } catch (error) {
                    failed = true;
                    localStorage.removeItem(jwtToken);
                    localStorage.removeItem('user');
                }

                if (failed) {
                    localStorage.setItem(jwtToken, JSON.stringify(userData));
                }

                return userData;
            }
        }

        return user;
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated,
                getUserDetails,
                login,
                logout,
                getUserDetailsNotAsync,
                setUserDetailsNotAsync,
                answer,
                logoutWithoutNavigate,
                passwordLessLogin,
                otpVerification,
                phoneOtpVerification,
                resendPhoneOtp,
                resendOtp,
                isSimpleAuthenticated,
                refreshSessionData,
                isOrderUpdateAuthenticated,
                isConfinedToOrderUpdate,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

const useAuth = () => {
    return useContext(AuthContext);
};

export { useAuth };
